// =============================================================================
// FAQ SECTION COMPONENT (V2 - NORMALIZED)
// =============================================================================

import React, { useState } from 'react';
import { ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';
import { layoutToClasses, displayToStyles } from '@/types/layout';
import type { ContentComponent } from '@/types';
import type { FAQComponent } from '@/types/components';

interface FAQSectionProps {
  component: ContentComponent;
}

/**
 * Type guard for new FAQComponent format
 */
function isNewFAQFormat(component: ContentComponent): boolean {
  return (
    component.type === 'faq' &&
    'items' in component &&
    Array.isArray((component as any).items)
  );
}

/**
 * FAQ Section Component
 */
export const FAQSection: React.FC<FAQSectionProps> = ({ component }) => {
  if (isNewFAQFormat(component)) {
    return <FAQSectionNew component={component as unknown as any} />;
  }
  return <FAQSectionLegacy component={component} />;
};

/**
 * New format FAQ Section
 */
const FAQSectionNew: React.FC<{ component: FAQComponent }> = ({ component }) => {
  const { id, content, items, layout, display } = component;
  
  const layoutClasses = layoutToClasses(layout);
  const displayStyles = displayToStyles(display);
  
  const [openItems, setOpenItems] = useState<Set<number>>(new Set());
  
  const toggleItem = (index: number) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(index)) {
      newOpenItems.delete(index);
    } else {
      newOpenItems.add(index);
    }
    setOpenItems(newOpenItems);
  };
  
  const groupFAQsByCategory = () => {
    const grouped: Record<string, typeof items> = {};
    items.forEach((faq) => {
      const category = faq.category || 'General';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(faq);
    });
    return grouped;
  };
  
  const groupedFAQs = groupFAQsByCategory();
  
  return (
    <section id={id} className={`content-section faq-section ${layoutClasses}`} style={displayStyles}>
      <div className="container">
        {content.title && (
          <div className="section-header">
            <h2>{content.title}</h2>
            {content.description && <p>{content.description}</p>}
          </div>
        )}
        
        <div className={`faq-grid grid-cols-${layout.columns || 1}`}>
          {Object.entries(groupedFAQs).map(([category, categoryFAQs]) => (
            <div key={category} className="faq-category">
              <div className="faq-category-header">
                <HelpCircle size={20} className="category-icon" />
                <h3 className="category-title">{category}</h3>
              </div>
              
              <div className="faq-list">
                {categoryFAQs.map((faq, index) => {
                  const globalIndex = items.findIndex(f => f === faq);
                  const isOpen = openItems.has(globalIndex);
                  
                  return (
                    <div key={faq.id || index} className="faq-item">
                      <button
                        className={`faq-question ${isOpen ? 'open' : ''}`}
                        onClick={() => toggleItem(globalIndex)}
                        aria-expanded={isOpen}
                      >
                        <span className="question-text">{faq.question}</span>
                        <div className="question-icon">
                          {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                        </div>
                      </button>
                      
                      <div className={`faq-answer ${isOpen ? 'open' : ''}`}>
                        <div className="answer-content">
                          <p>{faq.answer}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/**
 * Legacy format FAQ Section
 */
const FAQSectionLegacy: React.FC<{ component: ContentComponent }> = ({ component }) => {
  const { id, title, content, metadata } = component;
  
  const faqs = metadata?.faqs || [];
  const columns = metadata?.columns || 1;
  
  const [openItems, setOpenItems] = useState<Set<number>>(new Set());
  
  const toggleItem = (index: number) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(index)) {
      newOpenItems.delete(index);
    } else {
      newOpenItems.add(index);
    }
    setOpenItems(newOpenItems);
  };
  
  const groupFAQsByCategory = () => {
    const grouped: Record<string, any[]> = {};
    faqs.forEach((faq: any) => {
      const category = faq.category || 'General';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(faq);
    });
    return grouped;
  };
  
  const groupedFAQs = groupFAQsByCategory();
  
  return (
    <section id={id} className="content-section faq-section">
      <div className="container">
        {title && (
          <div className="section-header">
            <h2>{title}</h2>
            {content && <p>{content}</p>}
          </div>
        )}
        
        <div className={`faq-grid grid-cols-${columns}`}>
          {Object.entries(groupedFAQs).map(([category, categoryFAQs]) => (
            <div key={category} className="faq-category">
              <div className="faq-category-header">
                <HelpCircle size={20} className="category-icon" />
                <h3 className="category-title">{category}</h3>
              </div>
              
              <div className="faq-list">
                {categoryFAQs.map((faq: any, index: number) => {
                  const globalIndex = faqs.findIndex((f: any) => f === faq);
                  const isOpen = openItems.has(globalIndex);
                  
                  return (
                    <div key={index} className="faq-item">
                      <button
                        className={`faq-question ${isOpen ? 'open' : ''}`}
                        onClick={() => toggleItem(globalIndex)}
                        aria-expanded={isOpen}
                      >
                        <span className="question-text">{faq.question}</span>
                        <div className="question-icon">
                          {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                        </div>
                      </button>
                      
                      <div className={`faq-answer ${isOpen ? 'open' : ''}`}>
                        <div className="answer-content">
                          <p>{faq.answer}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export { FAQSectionNew, FAQSectionLegacy };

